/**
 * The name the browser matches on when morphing one element into another.
 *
 * Exactly one element may carry a given `view-transition-name` at a time, so
 * this is applied to the capsule being clicked — imperatively, in the click
 * handler — rather than to all two hundred of them. Naming every capsule would
 * be valid but would ask the browser to snapshot the entire grid.
 */
export const GAME_ART_TRANSITION = 'game-art'

/**
 * Marks an element as the thing being navigated from.
 *
 * Set on the DOM node directly rather than through state: the browser takes
 * its "before" snapshot as the navigation starts, and a React re-render would
 * not reliably have painted by then.
 */
export function markAsTransitionSource(element: HTMLElement | null): void {
  if (!element) return

  // Whatever carried it last is stale — two elements sharing a name cancels
  // the transition entirely rather than picking one.
  for (const previous of document.querySelectorAll<HTMLElement>('[data-view-transition]')) {
    previous.style.viewTransitionName = ''
    previous.removeAttribute('data-view-transition')
  }

  element.style.viewTransitionName = GAME_ART_TRANSITION
  element.dataset.viewTransition = 'source'
}

/**
 * Silences the one rejection a view transition is expected to produce.
 *
 * The router starts the transition itself, so there is nowhere to attach a
 * `.catch` — and an interrupted one rejects: navigating again before the
 * previous transition settles, or a document that is not rendering frames at
 * all. Neither is a fault, and neither leaves the app in a bad state; the
 * navigation still happens. Left alone it surfaces as an uncaught rejection
 * and would be noise in any error reporting.
 *
 * Deliberately narrow: it matches this one abort and re-throws everything
 * else, because a blanket handler here would hide real failures.
 */
export function ignoreAbortedViewTransitions(): void {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { name?: string; message?: string } | null

    const aborted =
      reason?.name === 'InvalidStateError' &&
      typeof reason.message === 'string' &&
      reason.message.includes('Transition was aborted')

    if (aborted) event.preventDefault()
  })
}
